import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import PageCard from "../../components/ui/PageCard";
import { getBookingsHub, BookingHubParams, BookingHubRow } from "../../api/bookingsService";
import { getCompanys } from "../../api/companysService";
import { getEvents } from "../../api/eventsService";
import { getPublications, getIssues } from "../../api/magazineService";

const BookingsHub = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<BookingHubRow[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [publications, setPublications] = useState<any[]>([]);
  const [issues, setIssues] = useState<any[]>([]);

  const [filters, setFilters] = useState<BookingHubParams>({
    type: "all",
    status: "",
    company_id: "",
    event_id: "",
    publication_id: "",
    issue_id: "",
    q: "",
    archived: "false",
  });

  const loadFilters = async () => {
    const [companyData, eventData, publicationData] = await Promise.all([
      getCompanys(""),
      getEvents(),
      getPublications(),
    ]);
    setCompanies(companyData);
    setEvents(eventData.filter((e: any) => e.deleted !== "yes"));
    setPublications(publicationData);
  };

  useEffect(() => {
    if (filters.publication_id) {
      getIssues(filters.publication_id).then(setIssues);
    } else {
      setIssues([]);
    }
  }, [filters.publication_id]);

  const load = async (params: BookingHubParams) => {
    setLoading(true);
    const query: BookingHubParams = {
      type: params.type || "all",
    };
    if (params.status) query.status = params.status;
    if (params.company_id) query.company_id = params.company_id;
    if (params.event_id) query.event_id = params.event_id;
    if (params.publication_id) query.publication_id = params.publication_id;
    if (params.issue_id) query.issue_id = params.issue_id;
    if (params.q) query.q = params.q;
    if (params.archived) query.archived = params.archived;

    const data = await getBookingsHub(query);
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    loadFilters();
    load(filters);
  }, []);

  const applyFilters = () => load(filters);

  const clearFilters = () => {
    const cleared: BookingHubParams = {
      type: "all",
      status: "",
      company_id: "",
      event_id: "",
      publication_id: "",
      issue_id: "",
      q: "",
      archived: "false",
    };
    setFilters(cleared);
    load(cleared);
  };

  const companyOptions = useMemo(
    () => [...companies].sort((a, b) => a.name.localeCompare(b.name)),
    [companies]
  );

  const eventOptions = useMemo(
    () => [...events].sort((a, b) => (a.title || "").localeCompare(b.title || "")),
    [events]
  );

  const typeLabel = (row: BookingHubRow) => (row.type === "event" ? "Event" : "Magazine");

  const contextLabel = (row: BookingHubRow) => {
    if (row.type === "event") return row.event?.title || "—";
    return [row.publication, row.issue].filter(Boolean).join(" — ") || "—";
  };

  return (
    <Layout>
      <div className="sticky top-[53px] z-20 -mx-1 mb-3 bg-[#f7f7f7] px-1">
        <div className="mb-2 pt-0.5">
          <h1 className="text-xl font-semibold text-gray-900">All bookings</h1>
          <p className="text-xs text-gray-500">Event exhibitor and magazine bookings (admin)</p>
        </div>
        <PageCard className="p-3 shadow-md">
          <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
            <div className="flex min-w-[88px] flex-col gap-0.5">
              <label className="text-xs font-medium text-gray-500">Type</label>
              <select
                className="h-8 min-w-0 rounded border border-gray-300 px-2 text-sm box-border"
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value as BookingHubParams["type"] })}
              >
                <option value="all">All</option>
                <option value="event">Event</option>
                <option value="magazine">Magazine</option>
              </select>
            </div>
            <div className="flex min-w-[100px] flex-col gap-0.5">
              <label className="text-xs font-medium text-gray-500">Status</label>
              <input
                className="h-8 min-w-0 rounded border border-gray-300 px-2 text-sm box-border"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                placeholder="Status"
              />
            </div>
            <div className="flex min-w-[140px] flex-1 flex-col gap-0.5 sm:max-w-[200px]">
              <label className="text-xs font-medium text-gray-500">Company</label>
              <select
                className="h-8 min-w-0 w-full rounded border border-gray-300 px-2 text-sm box-border"
                value={filters.company_id}
                onChange={(e) => setFilters({ ...filters, company_id: e.target.value })}
              >
                <option value="">All</option>
                {companyOptions.map((c) => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="flex min-w-[140px] flex-1 flex-col gap-0.5 sm:max-w-[200px]">
              <label className="text-xs font-medium text-gray-500">Event</label>
              <select
                className="h-8 min-w-0 w-full rounded border border-gray-300 px-2 text-sm box-border disabled:bg-gray-50"
                value={filters.event_id}
                onChange={(e) => setFilters({ ...filters, event_id: e.target.value })}
                disabled={filters.type === "magazine"}
              >
                <option value="">All</option>
                {eventOptions.map((e) => (
                  <option key={e.id} value={String(e.id)}>{e.title}</option>
                ))}
              </select>
            </div>
            <div className="flex min-w-[140px] flex-1 flex-col gap-0.5 sm:max-w-[200px]">
              <label className="text-xs font-medium text-gray-500">Publication</label>
              <select
                className="h-8 min-w-0 w-full rounded border border-gray-300 px-2 text-sm box-border disabled:bg-gray-50"
                value={filters.publication_id}
                onChange={(e) =>
                  setFilters({ ...filters, publication_id: e.target.value, issue_id: "" })
                }
                disabled={filters.type === "event"}
              >
                <option value="">All</option>
                {publications.map((p) => (
                  <option key={p.id} value={String(p.id)}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="flex min-w-[140px] flex-1 flex-col gap-0.5 sm:max-w-[200px]">
              <label className="text-xs font-medium text-gray-500">Issue</label>
              <select
                className="h-8 min-w-0 w-full rounded border border-gray-300 px-2 text-sm box-border disabled:bg-gray-50"
                value={filters.issue_id}
                onChange={(e) => setFilters({ ...filters, issue_id: e.target.value })}
                disabled={filters.type === "event" || !filters.publication_id}
              >
                <option value="">All</option>
                {issues.map((i) => (
                  <option key={i.id} value={String(i.id)}>{i.title}</option>
                ))}
              </select>
            </div>
            <div className="flex min-w-[100px] flex-col gap-0.5">
              <label className="text-xs font-medium text-gray-500">Archived</label>
              <select
                className="h-8 min-w-0 rounded border border-gray-300 px-2 text-sm box-border"
                value={filters.archived || "false"}
                onChange={(e) => setFilters({ ...filters, archived: e.target.value })}
                disabled={filters.type === "event"}
              >
                <option value="false">Active</option>
                <option value="true">Archived</option>
                <option value="all">All</option>
              </select>
            </div>
            <div className="flex min-w-[160px] flex-[2] flex-col gap-0.5 sm:min-w-[200px]">
              <label className="text-xs font-medium text-gray-500">Search</label>
              <input
                className="h-8 min-w-0 w-full rounded border border-gray-300 px-2 text-sm box-border"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Company, event, publication..."
              />
            </div>
            <div className="flex shrink-0 gap-2 pb-px">
              <button
                type="button"
                onClick={applyFilters}
                className="h-8 rounded bg-torch-primary px-3 text-sm font-medium text-white hover:bg-torch-primary-dark"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={clearFilters}
                className="h-8 rounded border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          </div>
        </PageCard>
      </div>

      <PageCard>
        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No bookings found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Company</th>
                  <th className="py-2 pr-4">Event / Publication</th>
                  <th className="py-2 pr-4">Detail</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Sales rep</th>
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={`${row.type}-${row.id}`} className="border-b border-gray-100">
                    <td className="py-3 pr-4 font-medium text-gray-900">{typeLabel(row)}</td>
                    <td className="py-3 pr-4 text-gray-900">{row.company?.name || "—"}</td>
                    <td className="py-3 pr-4 text-gray-600">{contextLabel(row)}</td>
                    <td className="py-3 pr-4 text-gray-600">{row.detail}</td>
                    <td className="py-3 pr-4 capitalize text-gray-600">{row.status || "—"}</td>
                    <td className="py-3 pr-4 text-gray-600">
                      {row.sales_rep?.username || row.sales_rep?.email || "—"}
                    </td>
                    <td className="py-3 pr-4 text-gray-600">
                      {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-3">
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to={row.detail_path}
                          className="text-sm text-torch-primary hover:underline"
                        >
                          View
                        </Link>
                        {row.company_path && (
                          <Link
                            to={row.company_path}
                            className="text-sm text-gray-600 hover:underline"
                          >
                            Company
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageCard>
    </Layout>
  );
};

export default BookingsHub;
